import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle, Copy, Eye, EyeSlash, FileText, ImageSquare, Lock, Paperclip,
  Plus, ShareNetwork, Trash, UserCircle, Users, X,
} from "@phosphor-icons/react";

const SHARE_BASE = `${window.location.origin}/share`;

const FORM_TYPES = [
  { v: "text", l: "Texto corto" },
  { v: "textarea", l: "Texto largo" },
  { v: "number", l: "Número" },
  { v: "date", l: "Fecha" },
  { v: "yesno", l: "Sí / No" },
  { v: "select", l: "Selección" },
];

// Modal para crear/gestionar landings públicas del asegurado o de un
// perjudicado del expediente.
export default function AseguradoLandingModal({ expediente, onClose }) {
  const [landings, setLandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [target, setTarget] = useState({ type: "asegurado", implicado_id: null });
  const [title, setTitle] = useState("");
  const [welcome, setWelcome] = useState("");
  const [allowUploads, setAllowUploads] = useState(true);
  const [allowMessages, setAllowMessages] = useState(true);
  const [questions, setQuestions] = useState([]);

  const perjudicados = (expediente?.implicados || []).filter((i) => i.tipo === "perjudicado");

  useEffect(() => {
    load();
    // Prefill welcome con un mensaje genérico
    const ed = expediente?.extracted_data || {};
    const nombrePila = (ed.asegurado_nombre || "").split(" ")[0] || "hola";
    setTitle(`Tu siniestro con ${ed.aseguradora || expediente?.detected_aseguradora || "tu aseguradora"}`);
    setWelcome(
      `Hola ${nombrePila}, soy tu perito asignado. Te paso este enlace privado para que puedas subir las fotos del siniestro y cualquier documento que tengas. Si necesitas algo, escríbeme aquí.`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/expedientes/${expediente.id}/landings`);
      setLandings(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando enlaces");
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    setCreating(true);
    try {
      const payload = {
        target_type: target.type,
        target_implicado_id: target.type === "perjudicado" ? target.implicado_id : null,
        title: title.trim() || null,
        welcome_message: welcome.trim() || null,
        allow_uploads: allowUploads,
        allow_messages: allowMessages,
        form_questions: questions.map((q, i) => ({
          id: q.id,
          label: q.label?.trim() || `Pregunta ${i + 1}`,
          type: q.type,
          required: !!q.required,
          help_text: q.help_text || null,
          options: (q.options || []).filter(Boolean),
        })),
      };
      const r = await api.post(`/expedientes/${expediente.id}/landings`, payload);
      setLandings((prev) => [r.data, ...prev]);
      toast.success("Enlace creado. Cópialo y envíaselo al asegurado.");
      // reset
      setQuestions([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando enlace");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (l) => {
    if (!confirm("¿Revocar este enlace? El asegurado dejará de poder acceder.")) return;
    try {
      await api.put(`/landings/${l.id}`, { revoked: true });
      toast.success("Enlace revocado");
      await load();
    } catch (e) {
      toast.error("Error revocando");
    }
  };

  const del = async (l) => {
    if (!confirm("¿Eliminar definitivamente este enlace y todos sus envíos?")) return;
    try {
      await api.delete(`/landings/${l.id}`);
      toast.success("Enlace eliminado");
      await load();
    } catch (e) {
      toast.error("Error eliminando");
    }
  };

  const copy = (l) => {
    const url = `${SHARE_BASE}/${l.token}`;
    navigator.clipboard?.writeText(url);
    toast.success("Enlace copiado al portapapeles");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="landing-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <div className="h-14 px-5 border-b border-border flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <UserCircle className="w-5 h-5 text-brand" weight="duotone" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted">Espacio del asegurado</div>
              <div className="font-display font-bold text-base tracking-tight">Enviar enlace privado</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-muted hover:text-foreground">
            <X className="w-4 h-4" weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* ===== CREAR NUEVO ===== */}
          <section className="border border-border rounded-lg p-4 bg-surface/50 space-y-4">
            <div className="flex items-baseline justify-between">
              <h3 className="font-display font-bold text-sm tracking-tight">Nuevo enlace</h3>
              <span className="text-[10px] text-muted">El asegurado verá la app branded sin acceder al ERP.</span>
            </div>

            <div>
              <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold">Destinatario</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                <PillToggle
                  active={target.type === "asegurado"}
                  onClick={() => setTarget({ type: "asegurado", implicado_id: null })}
                  testid="landing-target-asegurado"
                  icon={UserCircle}
                  label="Asegurado"
                />
                {perjudicados.map((p) => (
                  <PillToggle
                    key={p.id}
                    active={target.type === "perjudicado" && target.implicado_id === p.id}
                    onClick={() => setTarget({ type: "perjudicado", implicado_id: p.id })}
                    testid={`landing-target-perjudicado-${p.id}`}
                    icon={Users}
                    label={`Perjudicado · ${(p.nombre || "sin nombre").split(" ")[0]}`}
                  />
                ))}
              </div>
              {perjudicados.length === 0 && (
                <div className="mt-2 text-[11px] text-muted bg-zinc-50 border border-zinc-200 rounded-md px-2.5 py-2 flex items-start gap-1.5">
                  <Users className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-muted" weight="bold" />
                  <span>
                    Este expediente no tiene perjudicados registrados. Para enviar el enlace
                    a un perjudicado, primero <strong>añádelo</strong> en la card de
                    "Implicados · Perjudicados" de la pestaña Expediente (con su nombre y
                    teléfono). Una vez creado aparecerá aquí como destinatario.
                  </span>
                </div>
              )}
              {target.type === "perjudicado" && (
                <div className="mt-2 text-[11px] text-muted bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 inline-flex items-start gap-1.5">
                  <Lock className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-700" weight="bold" />
                  <span>Para el perjudicado se ocultan los apellidos y la dirección. Sólo verá el nombre de pila y municipio/provincia.</span>
                </div>
              )}
            </div>

            <div>
              <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold">Título</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="landing-title-input"
                placeholder="Ej. Tu siniestro con Mapfre"
              />
            </div>

            <div>
              <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold">Mensaje de bienvenida</label>
              <textarea
                value={welcome}
                onChange={(e) => setWelcome(e.target.value)}
                rows={3}
                data-testid="landing-welcome-input"
                placeholder="Hola, soy tu perito..."
                className="!text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-2 text-[12px]">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={allowUploads} onChange={(e) => setAllowUploads(e.target.checked)} data-testid="landing-allow-uploads" />
                <span className="inline-flex items-center gap-1"><Paperclip className="w-3 h-3" weight="bold" /> Permitir subir fotos/vídeos</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={allowMessages} onChange={(e) => setAllowMessages(e.target.checked)} data-testid="landing-allow-messages" />
                <span className="inline-flex items-center gap-1"><ImageSquare className="w-3 h-3" weight="bold" /> Permitir mensajes</span>
              </label>
            </div>

            {/* Form builder mini */}
            <div>
              <div className="flex items-center justify-between">
                <label className="!text-[10px] !uppercase !tracking-widest !text-muted !font-semibold">Preguntas adicionales (opcional)</label>
                <button
                  type="button"
                  onClick={() => setQuestions((q) => [...q, { id: crypto.randomUUID(), label: "", type: "text", required: false, options: [] }])}
                  data-testid="landing-add-question"
                  className="text-[11px] text-muted hover:text-foreground inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" weight="bold" /> Añadir pregunta
                </button>
              </div>
              {questions.length > 0 && (
                <div className="space-y-2 mt-2">
                  {questions.map((q, i) => (
                    <div key={q.id} className="bg-white border border-border rounded-md p-2.5 space-y-1.5" data-testid={`landing-question-${i}`}>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={q.label}
                          onChange={(e) => setQuestions((qs) => qs.map((x) => x.id === q.id ? { ...x, label: e.target.value } : x))}
                          placeholder="Texto de la pregunta..."
                          className="!flex-1 !text-[13px] !py-1"
                        />
                        <select
                          value={q.type}
                          onChange={(e) => setQuestions((qs) => qs.map((x) => x.id === q.id ? { ...x, type: e.target.value } : x))}
                          className="!text-[11px] !py-1 !w-32"
                        >
                          {FORM_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                        </select>
                        <button onClick={() => setQuestions((qs) => qs.filter((x) => x.id !== q.id))} className="text-muted hover:text-status-error-fg p-1">
                          <Trash className="w-3 h-3" weight="bold" />
                        </button>
                      </div>
                      {q.type === "select" && (
                        <input
                          type="text"
                          value={(q.options || []).join(",")}
                          onChange={(e) => setQuestions((qs) => qs.map((x) => x.id === q.id ? { ...x, options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } : x))}
                          placeholder="Opciones separadas por coma: Sí, No, A veces"
                          className="!text-[11px] !py-1"
                        />
                      )}
                      <label className="text-[10px] text-muted inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!q.required}
                          onChange={(e) => setQuestions((qs) => qs.map((x) => x.id === q.id ? { ...x, required: e.target.checked } : x))}
                        />
                        Obligatoria
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={create}
              disabled={creating || (target.type === "perjudicado" && !target.implicado_id)}
              data-testid="landing-create-btn"
              className="bg-foreground hover:bg-zinc-800 text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2 disabled:opacity-50"
            >
              <ShareNetwork className="w-4 h-4" weight="bold" /> {creating ? "Generando..." : "Generar enlace"}
            </button>
          </section>

          {/* ===== LANDINGS EXISTENTES ===== */}
          <section>
            <h3 className="font-display font-bold text-sm tracking-tight mb-2 flex items-center gap-2">
              Enlaces activos · {landings.length}
            </h3>
            {loading ? (
              <div className="text-sm text-muted py-6 text-center">Cargando...</div>
            ) : landings.length === 0 ? (
              <div className="text-sm text-muted py-6 text-center border border-dashed border-border rounded-lg">
                Aún no has creado ningún enlace. El que generes arriba aparecerá aquí.
              </div>
            ) : (
              <ul className="space-y-2">
                {landings.map((l) => <LandingRow key={l.id} l={l} onCopy={() => copy(l)} onRevoke={() => revoke(l)} onDelete={() => del(l)} expediente={expediente} />)}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}


function PillToggle({ active, onClick, label, icon: Icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-[12px] font-semibold px-3 py-1.5 rounded-full border transition-all inline-flex items-center gap-1.5 ${
        active
          ? "bg-foreground text-white border-foreground"
          : "bg-white text-muted border-border hover:border-foreground hover:text-foreground"
      }`}
    >
      <Icon className="w-3.5 h-3.5" weight="bold" /> {label}
    </button>
  );
}


function LandingRow({ l, onCopy, onRevoke, onDelete, expediente }) {
  const [showActivity, setShowActivity] = useState(false);
  const [activity, setActivity] = useState([]);
  const [loadingAct, setLoadingAct] = useState(false);

  const url = `${SHARE_BASE}/${l.token}`;
  const isPerjudicado = l.target_type === "perjudicado";
  const implicado = isPerjudicado && expediente?.implicados?.find((i) => i.id === l.target_implicado_id);
  const targetName = isPerjudicado
    ? `Perjudicado · ${(implicado?.nombre || "sin nombre").split(" ")[0]}`
    : "Asegurado";

  const toggleActivity = async () => {
    if (showActivity) {
      setShowActivity(false);
      return;
    }
    setLoadingAct(true);
    try {
      const r = await api.get(`/landings/${l.id}/activity`);
      setActivity(r.data || []);
    } finally {
      setLoadingAct(false);
      setShowActivity(true);
    }
  };

  const counts = {
    upload: activity.filter((a) => a.kind === "upload").length,
    message: activity.filter((a) => a.kind === "message").length,
    form: activity.filter((a) => a.kind === "form_response").length,
  };

  return (
    <li className="border border-border rounded-lg overflow-hidden bg-white" data-testid={`landing-row-${l.id}`}>
      <div className="px-3.5 py-3 flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-brand/10 border border-brand/20 flex items-center justify-center flex-shrink-0">
          {isPerjudicado ? <Users className="w-4 h-4 text-brand" weight="duotone" /> : <UserCircle className="w-4 h-4 text-brand" weight="duotone" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-[13px]">{targetName}</div>
            {l.revoked && (
              <span className="text-[9px] uppercase tracking-wider font-bold bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">Revocado</span>
            )}
            <span className="text-[10px] font-mono text-muted">
              {l.visit_count > 0 ? `Visto ${l.visit_count}×` : "Sin visitas"}
            </span>
          </div>
          <div className="text-[11px] text-muted truncate mt-0.5">{l.title || "Sin título"}</div>
          <div className="flex items-center gap-1.5 mt-2">
            <code className="text-[10px] font-mono bg-zinc-100 px-2 py-1 rounded text-zinc-700 truncate flex-1 min-w-0">{url}</code>
            <button onClick={onCopy} className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md bg-foreground text-white font-semibold" data-testid={`landing-copy-${l.id}`}>
              <Copy className="w-3 h-3" weight="bold" /> Copiar
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button onClick={toggleActivity} className="text-[10px] text-muted hover:text-foreground inline-flex items-center gap-1" data-testid={`landing-toggle-activity-${l.id}`}>
            {showActivity ? <><EyeSlash className="w-3 h-3" weight="bold" /> Ocultar</> : <><Eye className="w-3 h-3" weight="bold" /> Actividad</>}
          </button>
          {!l.revoked && (
            <button onClick={onRevoke} className="text-[10px] text-muted hover:text-amber-700" data-testid={`landing-revoke-${l.id}`}>Revocar</button>
          )}
          <button onClick={onDelete} className="text-[10px] text-muted hover:text-status-error-fg" data-testid={`landing-delete-${l.id}`}>Eliminar</button>
        </div>
      </div>

      {showActivity && (
        <div className="border-t border-border bg-zinc-50 p-3 space-y-2">
          {loadingAct ? (
            <div className="text-xs text-muted text-center py-2">Cargando...</div>
          ) : activity.length === 0 ? (
            <div className="text-xs text-muted text-center py-2 italic">Sin actividad todavía.</div>
          ) : (
            <>
              <div className="flex gap-3 text-[11px] text-muted">
                <span className="inline-flex items-center gap-1"><Paperclip className="w-3 h-3" weight="bold" /> {counts.upload} archivos</span>
                <span className="inline-flex items-center gap-1"><ImageSquare className="w-3 h-3" weight="bold" /> {counts.message} mensajes</span>
                {counts.form > 0 && <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" weight="bold" /> {counts.form} formularios</span>}
              </div>
              <ul className="space-y-1">
                {activity.slice(0, 12).map((a) => (
                  <li key={a.id} className="text-[12px] bg-white rounded border border-border px-2.5 py-1.5">
                    <div className="text-[10px] text-muted font-mono">
                      {new Date(a.created_at).toLocaleString("es-ES")}
                    </div>
                    {a.kind === "upload" && (
                      <div className="inline-flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-emerald-500" weight="fill" /> Subió <strong>{a.file_name}</strong> · <span className="text-muted">{prettyBytes(a.file_size || 0)}</span></div>
                    )}
                    {a.kind === "message" && (
                      <div className="text-zinc-800">"{a.text}"</div>
                    )}
                    {a.kind === "form_response" && (
                      <div className="text-zinc-800">
                        Respuestas:
                        <ul className="ml-3 mt-1 list-disc">
                          {Object.entries(a.form_data || {}).map(([k, v]) => (
                            <li key={k}><span className="text-muted">{k}:</span> {String(v) || <em>vacío</em>}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {a.kind === "visit" && (
                      <div className="text-muted">Visitó la página</div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </li>
  );
}

function prettyBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
